import { useAuth0 } from '@auth0/auth0-react';

type LoginOptions = {
  appState?: {
    returnTo?: string;
    organizationName?: string;
    activationSuccess?: boolean;
  };
  screenHint?: 'signup';
  loginHint?: string;
  connection?: string;
  invitationToken?: string;
  redirectUri?: string;
};

type AuthState = {
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (options?: LoginOptions) => Promise<void>;
  logout: () => void;
};

export function useAuth(): AuthState {
  const auth0 = useAuth0();

  return {
    isLoading: auth0.isLoading,
    isAuthenticated: auth0.isAuthenticated,
    login: (options?: LoginOptions) =>
      auth0.loginWithRedirect({
        appState: options?.appState,
        authorizationParams: {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          connection: options?.connection ?? import.meta.env.VITE_AUTH0_CONNECTION,
          screen_hint: options?.screenHint,
          login_hint: options?.loginHint,
          // Custom properties must be prepended with "ext-" for Auth0 to not ignore them
          'ext-invitation_token': options?.invitationToken,
        },
      }),
    logout: () =>
      auth0.logout({
        logoutParams: { returnTo: window.location.origin + '/login' },
      }),
  };
}

export function Auth0Exporter() {
  const { isAuthenticated, isLoading, getAccessTokenSilently, logout } = useAuth0();
  auth0Exports.setIsAuthenticated(isAuthenticated);
  auth0Exports.setGetAccessTokenSilently(getAccessTokenSilently);
  auth0Exports.setLogout((returnTo?: string) =>
    logout({ logoutParams: { returnTo: returnTo ?? window.location.origin + '/login' } }),
  );
  if (!isLoading) {
    auth0Exports.setReady();
  }
  return null;
}

type Auth0 = ReturnType<typeof useAuth0>;

let auth0IsAuthenticated = false;
let auth0GetAccessTokenSilently: Auth0['getAccessTokenSilently'] | null = null;
let auth0Logout: ((returnTo?: string) => void) | null = null;

let authReadyResolve: () => void;
const authReadyPromise = new Promise<void>((resolve) => {
  authReadyResolve = resolve;
});

export const auth0Exports = {
  getIsAuthenticated: () => auth0IsAuthenticated,
  setIsAuthenticated: (value: boolean) => (auth0IsAuthenticated = value),
  getAccessTokenSilently: () => auth0GetAccessTokenSilently,
  setGetAccessTokenSilently: (func: Auth0['getAccessTokenSilently']) =>
    (auth0GetAccessTokenSilently = func),
  getLogout: () => auth0Logout,
  setLogout: (func: (returnTo?: string) => void) => (auth0Logout = func),
  waitUntilReady: () => authReadyPromise,
  setReady: () => authReadyResolve(),
};
